import React from 'react';

import { Pencil, X } from 'lucide-react';

import { useGetEnhancedCustomRatesQuery } from '../../api';
import { Button } from '../common';

interface ViewRatesModalProps {
	configId: string;
	fileId?: string;
	onClose: () => void;
	onCustomize: (fileId?: string) => void;
}

const ViewRatesModal: React.FC<ViewRatesModalProps> = ({
	configId,
	fileId,
	onClose,
	onCustomize,
}) => {
	const {
		data: enhancedConfigResponse,
		isLoading,
		error,
	} = useGetEnhancedCustomRatesQuery(configId);
	const configData = enhancedConfigResponse?.data;

	// Helper function to check if a rate type is selected
	const hasRateType = (rateType: string) => {
		const selectedTypes = configData?.selected_rate_types || [
			configData?.rate_plan_type,
		];
		return selectedTypes?.includes(rateType) || false;
	};

	if (isLoading) {
		return (
			<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
				<div className="bg-white rounded-lg p-6">
					<div className="text-center">Loading rates...</div>
				</div>
			</div>
		);
	}

	if (error || !configData) {
		return (
			<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
				<div className="bg-white rounded-lg p-6">
					<div className="text-center text-red-600">Failed to load rates</div>
					<button
						onClick={onClose}
						className="mt-4 px-4 py-2 bg-gray-500 text-white rounded"
					>
						Close
					</button>
				</div>
			</div>
		);
	}

	const formatPercentValue = (value?: number | null, scale: boolean = true) => {
		if (value === null || value === undefined || Number.isNaN(value)) {
			return '--';
		}
		const numeric = Number(value);
		const display = scale ? (numeric > 1 ? numeric : numeric * 100) : numeric;
		return `${display.toFixed(2)}%`;
	};

	const formatCurrencyValue = (value?: number | null) => {
		if (value === null || value === undefined || Number.isNaN(value)) {
			return '--';
		}
		return `$${Number(value).toFixed(2)}`;
	};

	const getTieredValue = (source: any, key: string) => {
		if (!source) return undefined;
		return source[`tiered_rates_${key}`] ?? source[key];
	};

	const buildTierRow = (
		label: string,
		baseSource?: any,
		dbtSource?: any,
		mirror = false,
	) => {
		const buildGroup = (src: any) => ({
			qualified: {
				rate: formatPercentValue(getTieredValue(src, 'qualified_rate'), true),
				fee: formatCurrencyValue(getTieredValue(src, 'qualified_per_item')),
			},
			mid: {
				rate: formatPercentValue(
					getTieredValue(src, 'midqualified_rate'),
					true,
				),
				fee: formatCurrencyValue(getTieredValue(src, 'midqualified_per_item')),
			},
			non: {
				rate: formatPercentValue(
					getTieredValue(src, 'nonqualified_rate'),
					true,
				),
				fee: formatCurrencyValue(getTieredValue(src, 'nonqualified_per_item')),
			},
		});

		const base = buildGroup(baseSource || {});
		const dbt = mirror ? base : buildGroup(dbtSource || {});
		return { label, base, dbt };
	};

	const buildStandardRow = (label: string, source?: any) => ({
		label,
		rate: formatPercentValue(source?.discount_rate, true),
		fee: formatCurrencyValue(source?.authorization_fee),
	});

	const enhancedRates = (configData as any)?.rates;
	const isEnhancedRates = Boolean(enhancedRates);

	const buildLegacySections = () => {
		const tierRows = [
			buildTierRow(
				'Visa / Mastercard / Discover Credit',
				configData.custom_rates?.VISA?.tiered?.credit_only,
				configData.custom_rates?.VISA?.tiered?.with_debit,
			),
			buildTierRow(
				'Visa / Mastercard / Discover Debit',
				configData.custom_rates?.VISA_DEBIT?.tiered?.with_debit,
				configData.custom_rates?.VISA_DEBIT?.tiered?.with_debit,
				true,
			),
			buildTierRow(
				'American Express',
				configData.custom_rates?.AMERICAN_EXPRESS?.tiered,
				configData.custom_rates?.AMERICAN_EXPRESS?.tiered,
				true,
			),
			buildTierRow(
				'Pin Debit',
				configData.custom_rates?.PIN_DEBIT?.tiered,
				configData.custom_rates?.PIN_DEBIT?.tiered,
				true,
			),
		];

		const standardRows = [
			buildStandardRow(
				'Credit',
				configData.custom_rates?.VISA?.common ||
					configData.custom_rates?.MASTERCARD?.common ||
					configData.custom_rates?.DISCOVER?.common,
			),
			buildStandardRow(
				'Debit',
				configData.custom_rates?.VISA_DEBIT?.common ||
					configData.custom_rates?.MASTERCARD_DEBIT?.common ||
					configData.custom_rates?.DISCOVER_DEBIT?.common,
			),
			buildStandardRow(
				'American Express',
				configData.custom_rates?.AMERICAN_EXPRESS?.common,
			),
			buildStandardRow('Pin Debit', configData.custom_rates?.PIN_DEBIT?.common),
		];

		const tierSections = [
			{
				title: 'Qualified',
				rows: tierRows
					.map(
						(row) =>
							row && {
								label: row.label,
								rate: row.base.qualified.rate,
								fee: row.base.qualified.fee,
							},
					)
					.filter((row) => row && row.rate !== '--'),
			},
			{
				title: 'Mid Qualified',
				rows: tierRows
					.map(
						(row) =>
							row && {
								label: row.label,
								rate: row.base.mid.rate,
								fee: row.base.mid.fee,
							},
					)
					.filter((row) => row && row.rate !== '--'),
			},
			{
				title: 'Non Qualified',
				rows: tierRows
					.map(
						(row) =>
							row && {
								label: row.label,
								rate: row.base.non.rate,
								fee: row.base.non.fee,
							},
					)
					.filter((row) => row && row.rate !== '--'),
			},
		];

		const standardSections = [
			{
				title: 'Discount Rate & Authorization Fee',
				rows: standardRows
					.map(
						(row) => row && { label: row.label, rate: row.rate, fee: row.fee },
					)
					.filter((row) => row && row.rate !== '--'),
			},
		];

		return hasRateType('TIERED') ? tierSections : standardSections;
	};

	const buildEnhancedStandardRow = (label: string, card?: any) => {
		if (!card) return null;
		const rate = formatPercentValue(card?.discount_rate, false);
		const fee = formatCurrencyValue(card?.authorization_fee);
		return {
			label,
			rate,
			fee,
		};
	};

	const buildEnhancedStandardRowSameForAll = (
		label: string,
		sameForAll?: any,
	) => {
		if (!sameForAll) return null;
		const rate = formatPercentValue(sameForAll?.discount_rate, false);
		const fee = formatCurrencyValue(sameForAll?.authorization_fee);
		return {
			label,
			rate,
			fee,
		};
	};

	const buildEnhancedTierRow = (
		label: string,
		card: any,
		baseKey: string = 'credit_only',
		dbKey: string = 'with_debit',
	) => {
		if (!card) {
			return null;
		}
		const baseSource =
			card?.[baseKey] || card?.tiered || card?.credit_only || card?.with_debit;
		const dbSource = card?.[dbKey] || baseSource;
		if (!baseSource && !dbSource) {
			return null;
		}
		const buildGroup = (source: any) => ({
			qualified: {
				rate: formatPercentValue(source?.qualified_rate, false),
				fee: formatCurrencyValue(source?.qualified_per_item),
			},
			mid: {
				rate: formatPercentValue(source?.midqualified_rate, false),
				fee: formatCurrencyValue(source?.midqualified_per_item),
			},
			non: {
				rate: formatPercentValue(source?.nonqualified_rate, false),
				fee: formatCurrencyValue(source?.nonqualified_per_item),
			},
		});

		return {
			label,
			qualified: buildGroup(baseSource || {}).qualified,
			mid: buildGroup(baseSource || {}).mid,
			non: buildGroup(baseSource || {}).non,
		};
	};

	const buildEnhancedTierRowSameForAll = (
		label: string,
		sameForAll?: any,
		useCreditRates: boolean = true,
	) => {
		if (!sameForAll) return null;
		const rates = useCreditRates
			? sameForAll.credit_rates
			: sameForAll.debit_rates;
		if (!rates) return null;

		return {
			label,
			qualified: {
				rate: formatPercentValue(rates?.qualified_rate, false),
				fee: formatCurrencyValue(rates?.qualified_per_item),
			},
			mid: {
				rate: formatPercentValue(rates?.midqualified_rate, false),
				fee: formatCurrencyValue(rates?.midqualified_per_item),
			},
			non: {
				rate: formatPercentValue(rates?.nonqualified_rate, false),
				fee: formatCurrencyValue(rates?.nonqualified_per_item),
			},
		};
	};

	const buildEnhancedSections = () => {
		const standardRates = enhancedRates?.standard_rates;
		const tieredRates = enhancedRates?.tiered_rates;

		// Handle standard rates
		let standardRows: Array<{ label: string; rate: string; fee: string }> = [];

		if (standardRates?.mode === 'same_for_all' && standardRates?.same_for_all) {
			// Same for all standard rates
			standardRows = [
				buildEnhancedStandardRowSameForAll(
					'All Card Types',
					standardRates.same_for_all,
				),
			].filter(Boolean) as Array<{ label: string; rate: string; fee: string }>;
		} else if (standardRates?.by_card_type) {
			// Different by card type standard rates
			const standardByCard = standardRates.by_card_type;
			standardRows = [
				buildEnhancedStandardRow(
					'Credit',
					standardByCard.VISA ||
						standardByCard.MASTERCARD ||
						standardByCard.DISCOVER,
				),
				buildEnhancedStandardRow(
					'Debit',
					standardByCard.VISA_DEBIT ||
						standardByCard.MASTERCARD_DEBIT ||
						standardByCard.DISCOVER_DEBIT,
				),
				buildEnhancedStandardRow(
					'American Express',
					standardByCard.AMERICAN_EXPRESS,
				),
				buildEnhancedStandardRow('Pin Debit', standardByCard.PIN_DEBIT),
			].filter(Boolean) as Array<{ label: string; rate: string; fee: string }>;
		}

		// Handle tiered rates
		let tierRows: Array<{
			label: string;
			qualified: { rate: string; fee: string };
			mid: { rate: string; fee: string };
			non: { rate: string; fee: string };
		}> = [];

		if (tieredRates?.mode === 'same_for_all' && tieredRates?.same_for_all) {
			// Same for all tiered rates
			const sameForAll = tieredRates.same_for_all;
			tierRows = [
				buildEnhancedTierRowSameForAll('Credit', sameForAll, true),
				buildEnhancedTierRowSameForAll('Debit', sameForAll, false),
				buildEnhancedTierRowSameForAll('American Express', sameForAll, true),
				buildEnhancedTierRowSameForAll('Pin Debit', sameForAll, true),
			].filter(Boolean) as Array<{
				label: string;
				qualified: { rate: string; fee: string };
				mid: { rate: string; fee: string };
				non: { rate: string; fee: string };
			}>;
		} else if (tieredRates?.by_card_type) {
			// Different by card type tiered rates
			const tieredByCard = tieredRates.by_card_type;
			tierRows = [
				buildEnhancedTierRow(
					'Credit',
					tieredByCard.VISA,
					'credit_only',
					'with_debit',
				),
				buildEnhancedTierRow(
					'Debit',
					tieredByCard.VISA_DEBIT,
					'with_debit',
					'with_debit',
				),
				buildEnhancedTierRow(
					'American Express',
					tieredByCard.AMERICAN_EXPRESS,
					'tiered',
					'tiered',
				),
				buildEnhancedTierRow(
					'Pin Debit',
					tieredByCard.PIN_DEBIT,
					'tiered',
					'tiered',
				),
			].filter(Boolean) as Array<{
				label: string;
				qualified: { rate: string; fee: string };
				mid: { rate: string; fee: string };
				non: { rate: string; fee: string };
			}>;
		}

		const buildTierSections = (rows: typeof tierRows) => [
			{
				title: 'Qualified',
				rows: rows
					.map(
						(row) =>
							row && {
								label: row.label,
								rate: row.qualified.rate,
								fee: row.qualified.fee,
							},
					)
					.filter((row) => row && row.rate !== '--'),
			},
			{
				title: 'Mid Qualified',
				rows: rows
					.map(
						(row) =>
							row && { label: row.label, rate: row.mid.rate, fee: row.mid.fee },
					)
					.filter((row) => row && row.rate !== '--'),
			},
			{
				title: 'Non Qualified',
				rows: rows
					.map(
						(row) =>
							row && { label: row.label, rate: row.non.rate, fee: row.non.fee },
					)
					.filter((row) => row && row.rate !== '--'),
			},
		];

		// Handle different plan types
		if (hasRateType('IC_PLUS') && hasRateType('TIERED')) {
			// Show both standard and tiered sections for ALL plan type
			const sections = [];

			if (standardRows.length) {
				sections.push({
					title: 'Standard Rates - Discount Rate & Authorization Fee',
					rows: standardRows
						.map(
							(row) =>
								row && { label: row.label, rate: row.rate, fee: row.fee },
						)
						.filter((row) => row && row.rate !== '--'),
				});
			}

			if (tierRows.length) {
				sections.push(...buildTierSections(tierRows));
			}

			return sections;
		} else if (tierRows.length && hasRateType('TIERED')) {
			return buildTierSections(tierRows);
		} else if (standardRows.length) {
			return [
				{
					title: 'Discount Rate & Authorization Fee',
					rows: standardRows
						.map(
							(row) =>
								row && { label: row.label, rate: row.rate, fee: row.fee },
						)
						.filter((row) => row && row.rate !== '--'),
				},
			];
		}

		return [];
	};

	const sections = (
		isEnhancedRates ? buildEnhancedSections() : buildLegacySections()
	).filter((section) => section.rows.length > 0);

	const passThrough =
		(configData as any)?.pass_through_costs ||
		(configData as any)?.preference_data?.pass_through_costs;
	const assessmentEnabled = passThrough?.use_custom_assessment_rates ?? false;
	const amexBundled = passThrough?.use_bundled_amex_rate ?? false;

	return (
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4 font-primary">
			<div className="w-[800px] max-h-[90vh] bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-neutral-200 flex flex-col shadow-xl">
				{/* Fixed Header */}
				<div className="flex-shrink-0 p-5 flex justify-start items-center gap-2.5 border-b border-neutral-200">
					<div className="flex-1 text-neutral-900 text-lg font-medium leading-tight">
						Confirm Fee Structure
					</div>
					<button
						onClick={onClose}
						className="w-6 h-6 flex items-center justify-center rounded hover:bg-neutral-100"
						aria-label="Close"
					>
						<X className="w-5 h-5 text-neutral-500" />
					</button>
				</div>

				{/* Scrollable Content */}
				<div className="flex-1 overflow-y-auto">
					<table className="w-full text-sm border-collapse">
						<thead>
							<tr>
								<th className="w-48 text-left px-6 py-4 font-semibold text-neutral-800">
									Card Type
								</th>
								<th className="text-center px-6 py-4 font-semibold text-neutral-800">
									Rate (%)
								</th>
								<th className="text-center px-6 py-4 font-semibold text-neutral-800">
									Auth Fee ($)
								</th>
							</tr>
						</thead>
						<tbody>
							{sections.map((section) => (
								<React.Fragment key={section.title}>
									<tr className="bg-neutral-100">
										<td className="px-6 py-3 text-sm font-medium text-neutral-800">
											{section.title}
										</td>
										<td className="px-6 py-3 text-xs font-medium text-neutral-600 text-center">
											Rate (%)
										</td>
										<td className="px-6 py-3 text-xs font-medium text-neutral-600 text-center">
											Auth Fee ($)
										</td>
									</tr>
									{section.rows.map((row) => (
										<tr key={row.label} className="hover:bg-gray-50">
											<td className="px-6 py-4 text-sm font-medium text-neutral-800">
												{row.label}
											</td>
											<td className="px-6 py-4 text-sm font-semibold text-primary-600 text-center">
												{row.rate}
											</td>
											<td className="px-6 py-4 text-sm font-semibold text-primary-600 text-center">
												{row.fee}
											</td>
										</tr>
									))}
								</React.Fragment>
							))}
						</tbody>
					</table>

					<div className="self-stretch mt-4 [&>*]:px-5 [&>*]:py-3 [&>*:nth-child(odd)]:bg-neutral-100 [&>*:nth-child(odd)]:flex [&>*:nth-child(odd)]:items-center [&>*:nth-child(odd)]:justify-between [&>*:nth-child(even)]:border-b [&>*:nth-child(even)]:border-neutral-200 [&>*:last-child]:border-b-0">
						<div>
							<span className="text-base font-medium text-neutral-800">
								Assessment fees
							</span>
							<button
								onClick={() => onCustomize(fileId)}
								className="w-6 h-6 flex items-center justify-center rounded hover:bg-neutral-200"
								aria-label="Edit assessment fees"
							>
								<Pencil className="w-4 h-4 text-primary-600" />
							</button>
						</div>
						<div>
							<div className="flex items-center justify-between text-sm">
								<span className="text-neutral-800">Assessment fees</span>
								<span className="text-primary-600 font-semibold">
									{assessmentEnabled ? 'Yes' : 'No'}
								</span>
							</div>
						</div>

						<div className="border-t border-neutral-200">
							<span className="text-base font-medium text-neutral-800">
								Special Rate for American Express
							</span>
							<button
								onClick={() => onCustomize(fileId)}
								className="w-6 h-6 flex items-center justify-center rounded hover:bg-neutral-200"
								aria-label="Edit Amex rate"
							>
								<Pencil className="w-4 h-4 text-primary-600" />
							</button>
						</div>
						<div>
							<div className="flex items-center justify-between text-sm">
								<span className="text-neutral-800">Amex rate</span>
								<span className="text-primary-600 font-semibold">
									{amexBundled ? 'Yes' : 'No'}
								</span>
							</div>
						</div>
					</div>
				</div>

				{/* Fixed Footer */}
				<div className="flex-shrink-0 p-5 flex justify-between items-center gap-2.5 border-t border-neutral-200">
					<Button variant="outline" onClick={onClose} className="h-10 px-5">
						Cancel
					</Button>
					<Button
						variant="outline"
						onClick={() => onCustomize(fileId)}
						className="h-10 px-5 flex items-center gap-2"
					>
						<Pencil className="w-4 h-4" />
						<span>Customise</span>
					</Button>
				</div>
			</div>
		</div>
	);
};

export default ViewRatesModal;
